import { useEffect, useState } from 'react'
import { ScrollView } from 'react-native'

import { BottomView } from '@zet/common/components/bottomView'
import KeyBoardWrapper from '@zet/common/components/keyboardWrapper'
import LazyHOC from '@zet/common/components/lazyHoc'
import useMyCustomersStore from '@zet/customer/src/store/myCustomers'
import { BottomSheet, colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { Select } from '@zet/design-system/select'
import { B1, B2, B5, H5, SH1, SH3 } from '@zet/design-system/typography'
import { useExperiments } from '@zet/experiments'
import { i18n } from '@zet/localization'
import { DEVICE_HEIGHT, DEVICE_WIDTH } from '@zet/utils/Constants'
import { Controller, useForm } from 'react-hook-form'

import { VerifyEmploymentDetailsParams } from '../../api/functions'
import {
  useAddCustomerNextState,
  useFetchCustomerDetails,
  useFetchFieldsByCriteria,
} from '../../api/hooks'
import useVerifyEmploymentDetails from '../../api/hooks/useVerifyEmploymentDetails'
import AddCustomerBackHandlerHOC from '../../components/addCustomerBackHandlerHOC'

type DetailsCardSectionProps = {
  iconName: string
  label: string
  details: string
}

const DetailsCardSection = ({
  iconName,
  label,
  details,
  ...props
}: DetailsCardSectionProps) => {
  return (
    <XStack {...props} className=" items-center ">
      <Icon type={iconName} color={zetColors.grays[80]} />
      <Stack className=" ml-4/2 ">
        <B2 className=" text-grays-60 ">{label}</B2>
        <SH3 className=" text-grays-80 ">{details}</SH3>
      </Stack>
    </XStack>
  )
}

const EmploymentDetails = ({
  route,
}: {
  route: {
    params: {
      customerId: number
    }
  }
}) => {
  const { data } = useExperiments()
  const { partnerId } = useMyCustomersStore((state) => state)
  const [selectedOccupation, setSelectedOccupation] = useState<string>('')
  const [isOccupationSheetVisible, setIsOccupationSheetVisible] =
    useState<boolean>(false)
  const [employmentType, setEmploymentType] = useState<string>('')
  const [isEmploymentSheetVisible, setIsEmploymentSheetVisible] =
    useState<boolean>(false)
  const [bankName, setBankName] = useState<string>('')
  const [isBankSheetVisible, setIsBankSheetVisible] = useState<boolean>(false)
  const [annualIncome, setAnnualIncome] = useState<string>('')

  const { data: prefilledData } = useFetchCustomerDetails(
    route?.params?.customerId,
  )

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
    register,
    unregister,
    watch,
  } = useForm({
    mode: 'onChange',
    shouldUnregister: true,
  })

  const watchEmploymentType = watch('employmentType')

  useEffect(() => {
    if (watchEmploymentType === 'Not Working') {
      unregister('occupation')
      unregister('bankName')
    } else {
      register('occupation')
      if (prefilledData?.modeOfIncome !== 'Cash') register('bankName')
    }
  }, [register, unregister, watchEmploymentType])

  useEffect(() => {
    if (prefilledData?.modeOfIncome === 'Cash') unregister('bankName')
  }, [prefilledData])

  function selectBankName(value: string) {
    setValue('bankName', value?.toString(), {
      shouldValidate: true,
      shouldTouch: true,
    })
    setBankName(value)
  }
  function onChangeSelect(value: string) {
    setIsOccupationSheetVisible(false)
    setValue('occupation', value, {
      shouldValidate: true,
      shouldTouch: true,
    })
    setSelectedOccupation(value)
  }

  function selectEmploymentType(value: string) {
    setIsEmploymentSheetVisible(false)
    setValue('employmentType', value, {
      shouldValidate: true,
      shouldTouch: true,
    })
    setEmploymentType(value)
  }
  const { data: EmploymentFields } = useFetchFieldsByCriteria('employmentType')
  const { data: OccupationFields } =
    useFetchFieldsByCriteria('currentOccupation')
  const { data: BankFields } = useFetchFieldsByCriteria('bankName')

  const onSubmit = () => {
    const employmentObj = EmploymentFields?.fields?.find(
      (item) => item?.displayValue === getValues('employmentType'),
    )

    const occupationObj = OccupationFields?.fields?.find(
      (item) => item?.displayValue === getValues('occupation'),
    )
    const requestBodyData = {
      customerId: route.params.customerId,
      employmentType: employmentObj?.key,
      bankNameList: getValues('bankName')?.split(',') || null,
      currentOccupation: occupationObj?.key || null,
      annualIncome: String(getValues('income')),
      screenName: 'SHOWN_EMPLOYMENT_DETAILS_SCREEN',
      ...(partnerId ? { partnerId: partnerId as number } : {}),
      ...(data?.UI_isAddCustomerNewFlow
        ? { screenName: 'SHOWN_EMPLOYMENT_DETAILS_SCREEN' }
        : {}),
    }
    if (data?.UI_isAddCustomerNewFlow) {
      mutateWithExperiments(requestBodyData)
    } else {
      mutate(requestBodyData as VerifyEmploymentDetailsParams)
    }
  }
  const { mutate: mutateWithExperiments, isLoading: isExperimentsLoading } =
    useAddCustomerNextState()
  const { mutate, isLoading } = useVerifyEmploymentDetails()

  return (
    <LazyHOC>
      <AddCustomerBackHandlerHOC>
        <KeyBoardWrapper
          className="flex-1 bg-white "
          BottomSheet={
            <BottomView
              onPress={handleSubmit(onSubmit)}
              stepCount={
                !data?.UI_isAddCustomerNewFlow
                  ? 3
                  : route?.params?.screenName === 'SHOWN_BASIC_DETAILS_SCREEN'
                    ? 3
                    : 4
              }
              maxCount={data?.UI_isAddCustomerNewFlow ? 4 : 3}
              disabled={!isValid}
              loading={isLoading || isExperimentsLoading}
              buttonEvent={{
                eventName: 'click_action',
                payload: {
                  item_name: 'add_customer_step_3_continue',
                  customer_id: route.params.customerId,
                  customer_annual_income: getValues('income'),
                  customer_employment_type: getValues('employmentType'),
                  customer_occupation: getValues('occupation') || '',
                  customer_bank_name: getValues('bankName') || '',
                },
              }}
            />
          }
        >
          <Stack className="px-3">
            <Stack>
              <Stack className="h-3" />
              <H5>{i18n.t('employmentDetails.enter')}</H5>

              {!data?.UI_isAddCustomerNewFlow && (
                <Stack className=" border-grays-40 mt-3 rounded-lg border p-3  ">
                  {prefilledData?.nameAsPan && (
                    <DetailsCardSection
                      iconName="UserOutlined"
                      label={i18n.t('employmentDetails.nameAsPerPan')}
                      details={prefilledData?.nameAsPan}
                    />
                  )}
                  <DetailsCardSection
                    iconName="IdCard"
                    label="Customer PAN Number"
                    details={prefilledData?.panNo}
                    className={`${prefilledData?.nameAsPan && 'mt-3'}`}
                  />
                </Stack>
              )}
            </Stack>

            <Stack className="mt-4">
              <B2 className="text-grays-80 mb-2">
                {i18n.t('employmentDetails.type')}
                <B1 className="text-error-100 pl-1">*</B1>
              </B2>

              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Employment type is required',
                  },
                }}
                render={({ field: { onBlur, value } }) => (
                  <Pressable onPress={() => setIsEmploymentSheetVisible(true)}>
                    <Input
                      isEditable={false}
                      placeholder={i18n.t('employmentDetails.egSelfEmp')}
                      onBlur={onBlur}
                      iconLeft={
                        <Stack className=" ">
                          <Icon
                            type="WorkOutlined"
                            color={zetColors.grays[80]}
                          />
                        </Stack>
                      }
                      iconRight={
                        <Icon
                          type="ArrowDown"
                          color={zetColors.grays[80]}
                          className="mr-1"
                        />
                      }
                      className="justify-center"
                      style={{
                        color: zetColors.grays[100],
                        fontFamily: 'Graphie-Book',
                      }}
                      value={value}
                    />
                  </Pressable>
                )}
                name="employmentType"
              />
              {errors.employmentType?.message ? (
                <Stack className="-mt-3 mb-3">
                  <B5 className="text-error-100">
                    {errors.employmentType?.message}
                  </B5>
                </Stack>
              ) : null}
            </Stack>

            {/* {watchEmploymentType !== 'Not Working' && (
          <Stack className="">
            <B2 className="mb-2 text-grays-80">
              {i18n.t('employmentDetails.occu')}
              <B1 className="pl-1 text-error-100">*</B1>
            </B2>

            <Controller
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Occupation is required',
                },
              }}
              render={({ field: { onBlur, value } }) => (
                <Pressable
                  onPress={() => setIsOccupationSheetVisible(true)}
                >
                  <Input
                    isEditable={false}
                    placeholder="Eg: Government Employee"
                    onBlur={onBlur}
                    iconLeft={
                      <Stack className=" ">
                        <Icon type="WorkOutlined" color={zetColors.grays[80]} />
                      </Stack>
                    }
                    iconRight={
                      <Icon
                        type="ArrowDown"
                        color={zetColors.grays[80]}
                        className="mr-1"
                      />
                    }
                    className="justify-center"
                    style={{
                      color: zetColors.grays[100],
                      fontFamily: 'Graphie-Book',
                    }}
                    value={value}
                  />
                </Pressable>
              )}
              name="occupation"
            />
            {errors.occupation?.message ? (
              <Stack className="-mt-3 mb-3">
                <B5 className="text-error-100">{errors.occupation?.message}</B5>
              </Stack>
            ) : null}
          </Stack>
        )} */}

            <Stack className="">
              <B2 className="text-grays-80 mb-1">
                {i18n.t('employmentDetails.annual')}
                <B1 className="text-error-100 pl-1">*</B1>
              </B2>

              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Income is required',
                  },
                  min: {
                    value: 1,
                    message: 'Income is required',
                  },
                  max: {
                    value: 99999999,
                    message: 'Maximum limit exceeded',
                  },

                  minLength: 1,
                  maxLength: 8,
                  pattern: {
                    value: /^(0|[1-9]\d*)(\.\d+)?$/,
                    message: 'Please enter only numbers',
                  },
                  validate: (value) => value > 0,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    placeholder="Eg: ₹14,475"
                    onBlur={onBlur}
                    onChangeText={(value) => {
                      onChange(+value)
                      setAnnualIncome(value)
                    }}
                    keyboardType="number-pad"
                    value={annualIncome}
                    type={`${errors.income?.message ? 'error' : 'primary'}`}
                  />
                )}
                name="income"
              />
              {errors.income?.message ? (
                <Stack className="mt-2">
                  <B5 className="text-error-100">{errors.income?.message}</B5>
                </Stack>
              ) : null}
            </Stack>

            {prefilledData?.modeOfIncome !== 'Cash' &&
              watchEmploymentType !== 'Not Working' && (
                <Stack className="mt-4">
                  <B2 className="text-grays-80 mb-2">
                    {i18n.t('employmentDetails.salary')}
                    <B1 className="text-error-100 pl-1">*</B1>
                  </B2>

                  <Controller
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: 'Bank name is required',
                      },
                    }}
                    render={({ field: { onBlur, value } }) => (
                      <Pressable
                        onPress={() => setIsBankSheetVisible(true)}
                        eventName="click_action"
                        eventPayload={{
                          item_name: 'add_customer_select_bank_account_name',
                        }}
                      >
                        <Input
                          isEditable={false}
                          placeholder={i18n.t('employmentDetails.egSBI')}
                          onBlur={onBlur}
                          iconLeft={
                            <Stack className=" ">
                              <Icon
                                type="BankOutlined"
                                color={zetColors.grays[80]}
                              />
                            </Stack>
                          }
                          iconRight={
                            <Icon
                              type="ArrowDown"
                              color={zetColors.grays[80]}
                              className="mr-1"
                            />
                          }
                          className="  justify-center"
                          style={{
                            color: zetColors.grays[100],
                            fontFamily: 'Graphie-Book',
                          }}
                          value={bankName.toString().substring(0, 35)}
                          selection={{ start: 0, end: 0 }}
                        />
                      </Pressable>
                    )}
                    name="bankName"
                  />
                  {errors.bankName?.message ? (
                    <Stack className="-mt-3 mb-3">
                      <B5 className="text-error-100">
                        {errors.bankName?.message}
                      </B5>
                    </Stack>
                  ) : null}
                </Stack>
              )}

            <BottomSheet
              visible={isEmploymentSheetVisible}
              disableBottomSheet={() => setIsEmploymentSheetVisible(false)}
              onDismiss={() => setIsEmploymentSheetVisible(false)}
              sheetName="add_customer_employment_details_type"
            >
              <>
                <XStack className="border-grays-30 justify-between border-b px-3 py-2 ">
                  <XStack className=" items-center  ">
                    <Icon type="WorkOutlined" size={24} />
                    <SH1 className=" ml-[12] ">
                      {i18n.t('employmentDetails.type')}
                    </SH1>
                  </XStack>
                  <Pressable onPress={() => setIsEmploymentSheetVisible(false)}>
                    <Icon size={24} type="Close" />
                  </Pressable>
                </XStack>
                <ScrollView
                  style={{
                    maxHeight: DEVICE_HEIGHT - 200,
                  }}
                >
                  <Stack className="px-3">
                    <Stack className="">
                      <Select onChange={selectEmploymentType}>
                        {EmploymentFields?.fields?.map(
                          ({ value, key, displayValue }) => (
                            <Select.Option
                              key={key}
                              name={displayValue}
                              selectedValue={employmentType}
                              optionContainerStyle="h-[62] mt-3 w-full justify-center rounded-lg"
                            >
                              <SH1>{displayValue}</SH1>
                            </Select.Option>
                          ),
                        )}
                      </Select>
                    </Stack>
                  </Stack>
                </ScrollView>
              </>
            </BottomSheet>

            <BottomSheet
              visible={isOccupationSheetVisible}
              disableBottomSheet={() => setIsOccupationSheetVisible(false)}
              onDismiss={() => setIsOccupationSheetVisible(false)}
              sheetName="add_customer_employment_details_occupation"
            >
              <Stack className="mt-2 px-3">
                <XStack
                  className="border-grays-30 justify-between self-center border-b px-3 py-2 "
                  style={{ width: DEVICE_WIDTH }}
                >
                  <XStack className=" items-center ">
                    <Icon type={'WorkOutlined'} size={24} />
                    <SH1 className=" ml-[12] ">
                      {i18n.t('employmentDetails.occu')}
                    </SH1>
                  </XStack>
                  <Pressable
                    onPress={() => {
                      setIsOccupationSheetVisible(false)
                    }}
                  >
                    <Icon size={24} type="Close" />
                  </Pressable>
                </XStack>
                <ScrollView
                  style={{
                    maxHeight: DEVICE_HEIGHT - 200,
                  }}
                >
                  <Stack className="mt-3">
                    <Select onChange={onChangeSelect}>
                      {OccupationFields?.fields?.map(
                        ({ key, value, displayValue }, index) => (
                          <Select.Option
                            key={`${index}+${key}`}
                            name={displayValue}
                            selectedValue={selectedOccupation}
                            optionContainerStyle="h-[62] mb-3 w-full justify-center rounded-lg"
                          >
                            <SH1>{displayValue}</SH1>
                          </Select.Option>
                        ),
                      )}
                    </Select>
                  </Stack>
                </ScrollView>
              </Stack>
            </BottomSheet>

            <BottomSheet
              visible={isBankSheetVisible}
              disableBottomSheet={() => setIsBankSheetVisible(false)}
              onDismiss={() => setIsBankSheetVisible(false)}
              sheetName="add_customer_employment_details_salary"
            >
              <Stack className="px-3">
                <XStack
                  className="border-grays-30 justify-between self-center border-b px-3 py-2 "
                  style={{ width: DEVICE_WIDTH }}
                >
                  <XStack className=" items-center ">
                    <Icon type={'BankOutlined'} size={24} />
                    <SH1 className=" ml-[12] ">
                      {i18n.t('employmentDetails.salary')}
                    </SH1>
                  </XStack>
                  <Pressable
                    onPress={() => {
                      setIsBankSheetVisible(false)
                    }}
                  >
                    <Icon size={24} type="Close" />
                  </Pressable>
                </XStack>
                <ScrollView
                  style={{
                    maxHeight: 0.8 * DEVICE_HEIGHT,
                  }}
                >
                  <Stack className="mt-3">
                    <Select
                      value={bankName}
                      mode="multi"
                      onChange={selectBankName}
                    >
                      {BankFields?.fields?.map(
                        ({ key, value, displayValue }, index) => (
                          <Select.Option
                            key={`${index}+${key}`}
                            name={displayValue}
                            selectedValue={bankName}
                            optionContainerStyle="h-[62] mb-3 w-full justify-center rounded-lg"
                            showToggle={true}
                            iconColor={{
                              active: zetColors.primary[50],
                              inActive: zetColors.grays[60],
                            }}
                          >
                            <SH1>{displayValue}</SH1>
                          </Select.Option>
                        ),
                      )}
                    </Select>
                  </Stack>
                </ScrollView>

                <Stack className="">
                  <Button
                    onPress={() => setIsBankSheetVisible(false)}
                    className="w-full"
                    size="lg"
                  >
                    {i18n.t('employmentDetails.cont')}
                  </Button>
                </Stack>
              </Stack>
            </BottomSheet>
          </Stack>
        </KeyBoardWrapper>
      </AddCustomerBackHandlerHOC>
    </LazyHOC>
  )
}

export default EmploymentDetails
